import React, { useEffect, useState, useRef, useCallback } from "react";
import { getDeviceSize } from "../../exports/InteractiveUtils";
import Pulse from "../Loader/Pulse";
import { COLOR_VARS } from "../../exports/ColorVars";
import {
  OrderBook,
  ArrowRight2,
  ArrowLeft,
  Cross,
} from "../CustomComponents/SwiftIcon/Icons";
import ServerRequest from "../../exports/ServerRequest";
import { GetCookie } from "../../exports/CookieManagement";
import { priceFormat, formatSchemeName } from "../../exports/UtilFunctions";
import SwiftModal from "../CustomComponents/SwiftModal/SwiftModal";
import SipCease from "./SipCease";
import stopButton from "../../assets/icons/mfstop.svg";
import showButton from "../../assets/icons/show.svg";
import Badge from "../ui/badge";

const DEVICE_SIZE = getDeviceSize();
const STATUS = {
  ACTIVE: "Active",
};
const Sip = ({ customer }) => {
  const [loading, setLoading] = useState(true);
  const [investment, setInvestment] = useState([]);
  const [totalActiveAmount, setTotalActiveAmount] = useState(0);
  const [totalSips, setTotalSips] = useState(0);
  const [showCeased, setShowCeased] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState("Fetching your SIPs");
  const [opacityMsg, setOpacityMsg] = useState(true);
  const [accountDetails, setAccountDeails] = useState([]);
  const [activeCard, setActiveCard] = useState(null);
  const [popup, setPopup] = useState({
    open: false,
    sip: null,
    loading: false,
    step: "",
    confirmMessage: "",
    cancelMessage: "",
  });
  const openPopup = useCallback(
    (next) => setPopup((p) => ({ ...p, open: true, ...next })),
    [],
  );
  const resetPopup = () =>
    setPopup({
      open: false,
      sip: null,
      loading: false,
      step: "",
      confirmMessage: "",
      cancelMessage: "",
    });
  const setPopupLoading = useCallback(
    (loading) => setPopup((p) => ({ ...p, loading })),
    [],
  );

  const activeRows = investment.filter((sip) => sip.Status === "Active");
  const ceasedRows = investment.filter((sip) => sip.Status !== "Active");
  const ceasedRef = useRef(null);
  const account_token = GetCookie("token");
  const account_code = customer?.account_code;

  useEffect(() => {
    if (showCeased && ceasedRef.current) {
      const parent = ceasedRef.current.closest(".mf-container");
      if (parent) {
        parent.scrollTo({
          top: ceasedRef.current.offsetTop,
          behavior: "smooth",
        });
      }
    }
  }, [showCeased]);

  useEffect(() => {
    setLoading(true);
    setInvestment([]);
    setTotalActiveAmount(0);
    fetchClientMasterReport();
    fetchSipDetails();
  }, [account_token, account_code]);

  useEffect(() => {
    const loadingMessageInterval = setInterval(() => {
      setLoadingMsg((prev) =>
        prev === "Fetching your SIPs"
          ? "It may take some time"
          : "Fetching your SIPs",
      );
    }, 3000);

    const opacityInterval = setInterval(() => {
      setOpacityMsg((prev) => !prev);
    }, 1500);

    // Cleanup
    return () => {
      clearInterval(loadingMessageInterval);
      clearInterval(opacityInterval);
    };
  }, []);

  const fetchSipDetails = async () => {
    if (!account_token && !account_code) return;

    try {
      const url = customer
        ? `/mf/sip?account_code=${account_code}` // Superuser
        : `/mf/sip?token=${account_token}`; // Customer

      const response = await ServerRequest({ url, method: "get" });

      if (Array.isArray(response?.results)) {
        setInvestment(response.results);
        setTotalSips(response?.results.length);
        setTotalActiveAmount(response.totalActiveAmount || 0);
      } else {
        setInvestment([]);
        setTotalActiveAmount(0);
      }
    } catch (e) {
      console.error("SIP fetch error", e);
      setInvestment([]);
      setTotalActiveAmount(0);
    } finally {
      setLoading(false);
    }
  };

  async function fetchClientMasterReport() {
    if (!account_code && !account_token) return;

    try {
      const url = !customer
        ? `/nse/get-client-account?token=${account_token}`
        : `/nse/get-client-account?account_code=${account_code}`;

      const res = await ServerRequest({ url, method: "get" });

      setAccountDeails(res.data.report_data);
    } catch (err) {
      console.error(err);
    }
  }

  // Updated async cancel handler (triggered from modal)
  const handleSipCancel = async () => {
    setPopupLoading(true);
    openPopup({ open: true, isCancel: true });
    try {
      // const response = await ServerRequest({ url: `/mf/fetchordersipbyinternalreferenceno?internal_no=${row.internal_order_id}`, method: "get" });

      setPopup((p) => ({ ...p, sip: investment || [] }));
    } catch (e) {
      console.error("Cancel prefetch error", e);
    } finally {
      setPopupLoading(false);
    }
  };

  return (
    <div className="mf-wrapper">
      {loading ? (
        <div className="app__login__screen">
          <div className="app__login__process">
            <Pulse />
            {/* <p className={opacityMsg ? "fadeIn" : "fadeOut"}>{loadingMsg}</p> */}
          </div>
        </div>
      ) : (
        <>
          {investment.length > 0 ? (
            <>
              {DEVICE_SIZE === "S" ? (
                <h2 className="sip-header">
                  {/* Running Systematic Investments  <br /> */}
                  <p>
                    Total Amount <br />{" "}
                    <span>{priceFormat(totalActiveAmount)}</span>
                  </p>
                  <p>
                    Total SIPs <br /> <span>#{totalSips}</span>
                  </p>
                </h2>
              ) : (
                <h2 className="sip-header">
                  Running Systematic Investments (Total Amount :{" "}
                  {priceFormat(totalActiveAmount)})
                </h2>
              )}
            </>
          ) : (
            <></>
          )}
          <div
            className={`mf-container ${customer ? "short sip" : ""} mf-transaction-container mf-portfolio-container`}
          >
            <div className="mf-watchlist-card-container">
              {investment.length === 0 ? (
                <>
                  <div className="mf-empty">
                    <OrderBook size={40} color={COLOR_VARS["SWIFT_COLOR4"]} />
                    <div>
                      <p>No Investments found</p>
                      <span>Looks like you have't made any Transactions</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {DEVICE_SIZE === "S" ? (
                    <>
                      {activeRows.map((h, index) => {
                        return (
                          <InvestMentCard
                            key={index}
                            data={h}
                            isActive={activeCard === index}
                            onActivate={() => setActiveCard(index)}
                            onDeactivate={() => setActiveCard(null)}
                            isCustomer={customer}
                            onCancel={handleSipCancel}
                          />
                        );
                      })}
                      {showCeased &&
                        ceasedRows.map((h, index) => {
                          return (
                            <div
                              key={index}
                              ref={index === 0 ? ceasedRef : null}
                            >
                              <InvestMentCard data={h} customer={customer} />
                            </div>
                          );
                        })}
                    </>
                  ) : (
                    <>
                      <table>
                        <thead>
                          <tr>
                            <td className="scheme-name">Scheme Name</td>
                            <td className="">ISIN</td>
                            <td className="">Folio No</td>
                            <td className="category">Category</td>
                            <td className="">Amount</td>
                            <td className="">Installments</td>
                            {/* <td className="">Completed</td> */}
                            <td>Frequencys</td>
                            <td className="">Day</td>
                            <td className="">First Inst.</td>
                            <td className="">Last Inst.</td>
                            <td className="">Status</td>
                          </tr>
                        </thead>
                        <tbody className="">
                          {activeRows.map((h, index) => (
                            <tr
                              key={`active-${index}`}
                              className="yes_hover sip-table-row"
                            >
                              <td>{h["SchemeName"]}</td>
                              <td>{h["ISIN"]}</td>
                              <td>{h["FolioNumber"]}</td>
                              <td className="body-category">{h["Category"]}</td>
                              <td>{priceFormat(h["Amount"])}</td>
                              <td>
                                {h["Completed"]} / {h["Installments"]}
                              </td>
                              {/* <td>{h["Completed"]}</td> */}
                              <td>{h["Frequency"]}</td>
                              <td>{h["DayOfMonth"]}</td>
                              <td>{h["FirstInstallment"]}</td>
                              <td>{h["LastInstallment"]}</td>
                              <td>
                                <Badge variant="success" size="xs">
                                  {h.Status}
                                </Badge>
                              </td>
                            </tr>
                          ))}

                          {ceasedRows.map((h, index) => (
                            <tr
                              key={`ceased-${index}`}
                              ref={index === 0 ? ceasedRef : null}
                              className={`ceased-row ${showCeased ? "show" : "hide"}`}
                            >
                              <td>{h["SchemeName"]}</td>
                              <td>{h["ISIN"]}</td>
                              <td>{h["FolioNumber"]}</td>
                              <td>{h["Category"]}</td>
                              <td>{priceFormat(h["Amount"])}</td>
                              <td>
                                {h["Completed"]} / {h["Installments"]}
                              </td>
                              <td>{h["Frequency"]}</td>
                              <td>{h["DayOfMonth"]}</td>
                              <td>{h["FirstInstallment"]}</td>
                              <td>{h["LastInstallment"]}</td>
                              <td>
                                <Badge variant="error" size="xs">
                                  {h["Status"]}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Popup Modal for Stop SIP */}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
          {investment.length === 0 ? (
            <></>
          ) : (
            <div className="portfolio-mfwc-buttons">
              <button
                className="hover-btn hover-input-btn"
                onClick={() => setShowCeased(!showCeased)}
              >
                {/* <img src={showButton} alt="showButton" />{" "} */}
                {showCeased
                  ? "Hide Ceased SIPs"
                  : `Show Ceased SIPs (${ceasedRows.length})`}
              </button>

              {investment.some((inv) => inv.Status === "Active") && (
                <button
                  className="hover-btn hover-input-btn"
                  onClick={() => handleSipCancel()}
                >
                  {/* <img src={stopButton} alt="stopButton" /> */}
                  Stop SIP
                </button>
              )}
            </div>
          )}
        </>
      )}
      {popup.open && (
        <SwiftModal closeModal={resetPopup} top="2%">
          <div className="swift-mf-buysell-popup swift-mf-popup">
            {popup.loading ? (
              <div className="swift-mf-popup-loader">
                <Pulse />
                {/* <p>Processing...</p> */}
              </div>
            ) : (
              <>
                <SipCease
                  popupData={popup.sip}
                  resetPopup={resetPopup}
                  clientData={accountDetails}
                />
              </>
            )}
          </div>
        </SwiftModal>
      )}
    </div>
  );
};

export default Sip;

function InvestMentCard({
  data,
  isActive,
  onActivate,
  onDeactivate,
  isCustomer,
  onCancel,
}) {
  const {
    FolioNumber,
    Category,
    SchemeName,
    Amount,
    Installments,
    Completed,
    Frequency,
    FirstInstallment,
    Status,
  } = data;

  const cardRef = useRef();
  const [newSchemeName, setNewSchemeName] = useState(null);

  useEffect(() => {
    if (SchemeName) {
      setNewSchemeName(formatSchemeName(SchemeName));
    }
  }, [SchemeName]);
  useEffect(() => {
    function handleClickOutside(event) {
      if (cardRef.current && !cardRef.current.contains(event.target)) {
        onDeactivate();
      }
    }

    if (isActive) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isActive, onDeactivate]);

  return (
    // <div className="mf-watchlist-card" onClick={onActivate} ref={cardRef}>
    //   <div className="mfwc-info">
    //     <div className="isin-price">
    //       <p className="isin-type">
    //         Folio: <span>{FolioNumber}</span>
    //       </p>
    //       <p style={{ fontSize: "13px", fontWeight: "700" }}>{Status}</p>
    //     </div>
    //     <p className="name">
    //       {SchemeName}
    //     </p>
    //     <p className="category">
    //       {Category}
    //     </p>
    //   </div>
    //   <div className="mfwc-config">
    //     <div className="mfwc-config-row">
    //       <p>
    //         Amount
    //         <span>{priceFormat(Amount)}</span>
    //       </p>
    //       <p>
    //         Frequency
    //         <span>{Frequency}</span>
    //       </p>
    //     </div>
    //     <div className="mfwc-config-row">
    //       <p>
    //         Installments
    //         <span>
    //           {Completed}&nbsp;out of&nbsp;{Installments}
    //         </span>
    //       </p>
    //       <p>
    //         Last Intallment
    //         <span>
    //           {FirstInstallment}
    //         </span>
    //       </p>
    //     </div>
    //   </div>
    //   {isCustomer && (<div className="hover-wrapper">
    //     {isActive && (<div className="sp__hover__options">
    //       {data.Status === STATUS.ACTIVE && (
    //         <div
    //           className="sp__hover__buttons stop"
    //           onClick={() => {
    //             onCancel(data);
    //           }}
    //         >
    //           Stop SIP
    //         </div>
    //       )}
    //     </div>)}
    //   </div>)}
    // </div>

    <div className="mf-watchlist-card" onClick={onActivate} ref={cardRef}>
      <div className="mfwc-config">
        <div className="mfwc-config-row">
          <p className="mfwc-title-row">
            <p className="name">{newSchemeName}</p>
            <p className="category">
              {Category},&nbsp;Folio: {FolioNumber}
            </p>
          </p>
          <p
            className="value"
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            {priceFormat(Amount)} <br />
            <Badge
              variant={Status === "Active" ? "success" : "error"}
              size="xxxs"
            >
              {Status}
            </Badge>
          </p>
        </div>
        <div className="mfwc-config-row">
          <p>
            Installments&nbsp;-&nbsp;
            <span>
              {Completed}&nbsp;/&nbsp;{Installments},&nbsp;{Frequency}
            </span>
          </p>
          <p>
            Last Intallment&nbsp;-&nbsp;
            <span>{FirstInstallment}</span>
          </p>
        </div>
      </div>
      {isCustomer && (
        <div className="hover-wrapper">
          {isActive && (
            <div className="sp__hover__options">
              {data.Status === STATUS.ACTIVE && (
                <div
                  className="sp__hover__buttons stop"
                  onClick={() => {
                    onCancel(data);
                  }}
                >
                  Stop SIP
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
